/**
 * Form-state model + projection helpers for the editable questionnaire
 * form (PROD-845 PR-2).
 *
 * Design: two parallel trees during editing —
 *   1. The DEFINITION (from `useQuestionnaireDefinition`) is immutable
 *      and drives rendering: question text, required flag, answer
 *      options, maxLength, recursive Group structure.
 *   2. The FORM STATE (held in the tab component's local state) mirrors
 *      the definition tree but holds the user's in-progress answers.
 *
 * On save, the form state is projected to the wire payload
 * (`QuestionnaireResponseSavePayload`) via `formStateToWirePayload`.
 *
 * Plan A (PROD-845): the completeness check recurses properly into
 * nested Groups — fixes the Flutter bug at
 * `appointment_form_widget.dart:127-130` (which treated Groups as
 * Strings in the flat loop). Backend doesn't validate completeness
 * (`QuestionnaireResponseService.cs` 2026-05-20), so this is a pure
 * client-side UX gate — tightening it client-side doesn't change wire
 * behaviour.
 */

import type {
  QuestionnaireDefinition,
  QuestionnaireDefinitionItem,
  QuestionnaireItemType,
  QuestionnaireResponseItemPayload,
  QuestionnaireResponseSavePayload,
  QuestionnaireValueCoding,
} from '@/types/scheduling-api';

/**
 * One item in the editable form's local state. Mirrors the definition
 * shape but carries the user's in-progress answer in `valueString` /
 * `valueBoolean` / `valueCoding`.
 *
 * Distinguishing "answered" from "default" for Boolean items: we use
 * `valueBoolean: null` to mean unanswered (vs `true`/`false` once the
 * user picks). At save time, `formStateToWirePayload` coerces `null` →
 * `false` to match the wire shape.
 */
export interface QuestionnaireFormItem {
  questionId: string;
  linkId: string;
  text: string;
  type: QuestionnaireItemType | string;
  required: boolean;
  valueString: string;
  /** `null` while unanswered (the wire allows only `true`/`false`; we
   *  coerce on save). */
  valueBoolean: boolean | null;
  valueCoding: QuestionnaireValueCoding[];
  /** Nested items for `type === 'Group'`. Empty for non-Group items. */
  items: QuestionnaireFormItem[];
  /** Backend-defined character cap for String items (PROD-846).
   *  `undefined` / `<= 0` means no cap. Surfaced via `<Textarea maxLength>`
   *  + character counter. Meaningful only when `type === 'String'`. */
  maxLength?: number;
}

/**
 * Project a definition tree to an initial blank form-state tree.
 * Walks recursively into Group children. Definitions with missing
 * critical fields (no `id` or no `type`) are dropped — render-safe
 * even if the backend ships an oddly-shaped item.
 */
export function definitionToFormState(
  definition: QuestionnaireDefinition | null | undefined,
): QuestionnaireFormItem[] {
  return (definition?.items ?? []).flatMap(definitionItemToFormItem);
}

function definitionItemToFormItem(
  d: QuestionnaireDefinitionItem,
): QuestionnaireFormItem[] {
  const id = d.id;
  const type = d.type;
  if (!id || !type) return [];
  return [
    {
      questionId: id,
      linkId: d.linkId ?? '',
      text: d.text ?? '',
      type,
      required: d.required === true,
      valueString: '',
      valueBoolean: null,
      valueCoding: [],
      items:
        type === 'Group'
          ? (d.items ?? []).flatMap(definitionItemToFormItem)
          : [],
      // PROD-846: forward backend `maxLength` for String items only.
      // For non-String items the field is meaningless (Choice answer
      // texts come from `answerOptions` server-side and have no length
      // cap from the form's perspective). `undefined` means "no cap".
      maxLength:
        type === 'String' && typeof d.maxLength === 'number' && d.maxLength > 0
          ? d.maxLength
          : undefined,
    },
  ];
}

// ── Completeness check (Plan A — recurse properly into Groups) ──────────

/**
 * Plan A: a form is complete when every REQUIRED leaf (non-Group)
 * item has a valid answer per its type, with the check recursing
 * properly into Group children. Group items themselves are never
 * directly "required" — they have no answer of their own.
 *
 * Differs from Flutter's bug at `appointment_form_widget.dart:127-130`,
 * which treated Group items as String items in the flat loop (and so
 * skipped their children entirely). Backend doesn't validate, so the
 * divergence is safe on the wire — see
 * `QuestionnaireResponseService.cs` 2026-05-20 read.
 */
export function isQuestionnaireComplete(items: QuestionnaireFormItem[]): boolean {
  return items.every(isItemComplete);
}

function isItemComplete(item: QuestionnaireFormItem): boolean {
  // Recurse into Groups regardless of their own `required` flag — what
  // matters is the required leaves inside.
  if (item.type === 'Group') {
    return isQuestionnaireComplete(item.items);
  }
  // Non-required leaves always count as complete.
  if (!item.required) return true;
  return isItemAnswered(item);
}

/**
 * "Answered" definition per question type. Exported for unit tests +
 * the per-item rendering ("answer required" indicator).
 */
export function isItemAnswered(item: QuestionnaireFormItem): boolean {
  switch (item.type) {
    case 'String':
      return (item.valueString ?? '').trim().length > 0;
    case 'Boolean':
      return item.valueBoolean === true || item.valueBoolean === false;
    case 'Choice':
    case 'OpenChoice':
      return (item.valueCoding ?? []).length > 0;
    case 'Group':
      // Group answeredness is the conjunction of its children.
      return isQuestionnaireComplete(item.items);
    default:
      // Unknown type — defensive false so an oddly-typed required item
      // doesn't silently let save through.
      return false;
  }
}

// ── Max nesting depth + validation ──────────────────────────────────────

/**
 * Hard cap on Group recursion depth. Real questionnaires nest 1-2
 * levels; anything past this strongly suggests a malformed/cyclic
 * backend response. The form REFUSES to render (showing a visible
 * error banner) rather than silently truncating — see PR #262 Samer
 * review §5: silent truncation would let the user submit a form
 * where the deep questions were never visible, which the backend
 * would record as `Completed` without those answers.
 */
export const MAX_QUESTIONNAIRE_NESTING_DEPTH = 10;

/**
 * Compute the maximum Group-nesting depth across the form. Empty
 * tree → 0. Used by `EditableQuestionnaireForm` (and the read-only
 * view) to refuse to render if the depth exceeds the cap.
 */
export function getMaxNestingDepth(
  items: ReadonlyArray<QuestionnaireFormItem | { type?: string; items?: ReadonlyArray<unknown> | null }>,
): number {
  let max = 0;
  const walk = (
    list: ReadonlyArray<{ type?: string; items?: ReadonlyArray<unknown> | null }>,
    depth: number,
  ): void => {
    if (depth > max) max = depth;
    for (const item of list) {
      if (item.type === 'Group' && Array.isArray(item.items) && item.items.length > 0) {
        walk(
          item.items as ReadonlyArray<{ type?: string; items?: ReadonlyArray<unknown> | null }>,
          depth + 1,
        );
      }
    }
  };
  walk(items as ReadonlyArray<{ type?: string; items?: ReadonlyArray<unknown> | null }>, 0);
  return max;
}

// ── Form-state mutation helpers ─────────────────────────────────────────

/**
 * Immutable update of a single item by `questionId`, walking into
 * nested Groups. Returns a new tree; the original is untouched. The
 * returned tree always has new references along the path to the
 * updated item so React's setState compares correctly.
 */
export function updateItem(
  items: QuestionnaireFormItem[],
  questionId: string,
  patch: Partial<QuestionnaireFormItem>,
): QuestionnaireFormItem[] {
  return items.map((item) => {
    if (item.questionId === questionId) {
      return { ...item, ...patch };
    }
    if (item.type === 'Group' && item.items.length > 0) {
      return { ...item, items: updateItem(item.items, questionId, patch) };
    }
    return item;
  });
}

// ── Save-payload projection ─────────────────────────────────────────────

export type FormStateToWireResult =
  | { ok: true; payload: QuestionnaireResponseSavePayload }
  | { ok: false; reason: 'incomplete' };

/**
 * Project the form-state tree to the save wire shape. Returns
 * `{ ok: false, reason: 'incomplete' }` if any required leaf is
 * unanswered (defence-in-depth — `isQuestionnaireComplete` is the
 * primary Save-button gate, this is the second check to ensure the
 * payload builder and the validator can't drift).
 *
 * Unanswered NON-required items are FILTERED OUT of the payload
 * entirely — see PR #262 Samer review (PHI correctness):
 *   - For a Boolean question, coercing `null → false` on the wire
 *     would silently record "patient answered No" in the patient's
 *     record. The backend writes whatever it receives without
 *     completeness validation
 *     (`QuestionnaireResponseService.cs:205-228`), so a fabricated
 *     answer becomes PHI.
 *   - For String / Choice / OpenChoice, an empty value silently
 *     records "patient answered with empty text / no selection" which
 *     is at best meaningless and at worst clinically misleading.
 * Filtering is safe because the backend tolerates partial payloads —
 * items not in the request just aren't recorded.
 *
 * Groups are filtered RECURSIVELY: a Group whose children are all
 * filtered out becomes an empty Group with `items: []`. We then drop
 * the Group entirely (no point recording an empty Group).
 */
export function formStateToWirePayload(
  formItems: QuestionnaireFormItem[],
  meta: {
    questionnaireId: string;
    subject: string;
    author: string;
    appointmentId: string;
    sourceId?: string | null;
  },
): FormStateToWireResult {
  // Second-line-of-defence completeness check. The Save button gate
  // uses the same function — if these two ever drift, this catches it
  // and refuses to ship a payload.
  if (!isQuestionnaireComplete(formItems)) {
    return { ok: false, reason: 'incomplete' };
  }
  const items = filterAndProjectItems(formItems);
  return {
    ok: true,
    payload: {
      questionnaireId: meta.questionnaireId,
      subject: meta.subject,
      author: meta.author,
      sourceId: meta.sourceId ?? null,
      appointmentId: meta.appointmentId,
      items,
    },
  };
}

/**
 * Project + filter the form-state items into the wire shape. Unanswered
 * non-required items are dropped; Groups whose children all drop out
 * become empty Groups which are then dropped too.
 */
function filterAndProjectItems(
  items: QuestionnaireFormItem[],
): QuestionnaireResponseItemPayload[] {
  const out: QuestionnaireResponseItemPayload[] = [];
  for (const item of items) {
    if (item.type === 'Group') {
      const projectedChildren = filterAndProjectItems(item.items);
      // Drop a Group with no surviving children — recording an empty
      // Group adds nothing.
      if (projectedChildren.length === 0) continue;
      out.push({
        questionId: item.questionId,
        linkId: item.linkId,
        text: item.text,
        type: item.type,
        // Group items don't carry an answer; ship the empty canonical
        // shape Flutter uses on the wire.
        answer: { valueBoolean: false, valueString: '', valueCoding: [] },
        items: projectedChildren,
      });
      continue;
    }
    if (!isItemAnswered(item)) continue;
    out.push({
      questionId: item.questionId,
      linkId: item.linkId,
      text: item.text,
      type: item.type,
      answer: buildAnswerPayload(item),
      items: [],
    });
  }
  return out;
}

/**
 * Build the per-item `answer` payload. The wire requires all 3
 * `value*` fields per the 2026-05-20 save curl. ONLY called for items
 * that pass `isItemAnswered` — so `valueBoolean` is guaranteed
 * non-null for Boolean items, `valueString` non-empty for String,
 * `valueCoding` non-empty for Choice/OpenChoice. That guarantee is
 * the reason we can safely coerce `valueBoolean ?? false` here
 * without fabricating PHI: the filter upstream rejected unanswered
 * Boolean items already.
 */
function buildAnswerPayload(item: QuestionnaireFormItem): {
  valueBoolean: boolean;
  valueString: string;
  valueCoding: typeof item.valueCoding;
} {
  return {
    // Safe: filter upstream rejects items where valueBoolean is null
    // AND type === 'Boolean'. For other types valueBoolean is
    // initialised to null and not meaningful — coerce to false.
    valueBoolean: item.valueBoolean === true,
    valueString: item.valueString ?? '',
    valueCoding: item.valueCoding ?? [],
  };
}
